import { useTranslation } from "next-i18next";

import { Currency } from "@/Apollo/schema";
import { Flavor } from "@/ui/typography/Flavor";

type UsDeliveryPriceProps = {
  label: string;
  /** In the display currency; `unconverted` is the euro amount charged, when converted. */
  price: {
    amount: number;
    currency: Currency;
    unconverted?: { amount: number; currency: Currency } | null;
  };
  /** An estimate (the postal rate): the spoken sentence hedges as the visible copy does. */
  estimate?: boolean;
  "data-testid"?: string;
};

/**
 * WHO-6509. A US delivery price (the quote fee, the postal rate) as the item price above it shows
 * money on `/en-us` (WHO-5142, Andrew 4 Nov): the display currency first, the euro it came from
 * small beside it. Euros are what is charged, so a converted price carries checkout's own note
 * that the buyer pays in euros (`checkout-converted-total-note`, shipped to the product page as a
 * one-key subset). No conversion (EUR, or no rate): the euro price alone, as checkout shows it.
 *
 * Two bare amounts side by side don't say which one is charged, so a screen reader gets a sentence
 * instead of the pair. For an estimate it doesn't promise the amount charged: checkout prices
 * the buyer's own address.
 */
export const UsDeliveryPrice = ({
  label,
  price,
  estimate = false,
  "data-testid": testId,
}: UsDeliveryPriceProps) => {
  const { t } = useTranslation();
  const charged = price.unconverted;

  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3">
        <Flavor size="medium">{label}</Flavor>
        <span className="flex items-baseline gap-2">
          <span className="flex items-baseline gap-2" aria-hidden={!!charged}>
            <Flavor size="medium" className="font-bold">
              {t("common:common-price", { price })}
            </Flavor>
            {charged && (
              <span
                className="font-body text-sm text-grey-500"
                data-testid={testId && `${testId}.unconverted`}
              >
                {t("common:common-price", { price: charged })}
              </span>
            )}
          </span>
          {charged && (
            <span className="sr-only" data-testid={testId && `${testId}.sr`}>
              {estimate
                ? t("pdp:pdp-us-delivery-price-estimated-sr", {
                    price,
                    charged,
                    defaultValue:
                      "Estimated delivery cost: about {{price, currency}} ({{charged, currency}}), calculated at checkout for your address.",
                  })
                : t("pdp:pdp-us-delivery-price-charged-sr", {
                    price,
                    charged,
                    defaultValue:
                      "About {{price, currency}}. You are charged {{charged, currency}}.",
                  })}
            </span>
          )}
        </span>
      </div>
      {charged && (
        <span className="self-end font-body text-xs text-grey-700">
          {t("checkout:checkout-converted-total-note", {
            defaultValue:
              "You pay in euros. Your bank converts at its own rate.",
          })}
        </span>
      )}
    </div>
  );
};
